/**
 * Content Editor Page - Simple CMS for marketing pages
 * Allows editing text and images on marketing pages without code changes
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft,
  Save,
  RefreshCw,
  Edit3,
  Type,
  Image,
  List,
  RotateCcw,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Eye,
  FileText,
  Home,
  Info,
  HelpCircle,
  DollarSign,
  MessageSquare,
  Plus,
  Trash2,
  Upload,
  ImagePlus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Section icons mapping
const SECTION_ICONS = {
  hero: Home,
  features: List,
  pricing: DollarSign,
  testimonials: MessageSquare,
  contact: MessageSquare,
  about_hero: Info,
  about_story: FileText
};

// Section display names
const SECTION_NAMES = {
  hero: 'Hero Section',
  features: 'Features Section',
  pricing: 'Pricing Plans',
  testimonials: 'Testimonials',
  contact: 'Contact Info',
  about_hero: 'About Page Hero',
  about_story: 'About Page Story'
};

// Page groupings
const PAGE_GROUPS = {
  home: ['hero', 'features', 'pricing', 'testimonials', 'contact'],
  about: ['about_hero', 'about_story']
};

const ContentEditorPage = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [hasChanges, setHasChanges] = useState({});
  const [activeTab, setActiveTab] = useState('home');
  const [uploadingImage, setUploadingImage] = useState(null);

  // Check if user is master admin - redirect if not
  useEffect(() => {
    const checkAdminContext = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/admin/context`);
        if (response.data.is_master === false) {
          toast.error('Access denied. Only master admin can edit marketing content.');
          navigate('/admin');
        }
      } catch (error) {
        // If we can't verify, allow access (backward compatibility)
        console.error('Failed to check admin context:', error);
      }
    };
    checkAdminContext();
  }, [navigate]);

  // Upload image
  const uploadImage = async (file, sectionId, path) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }
    
    setUploadingImage(`${sectionId}-${path}`);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${BACKEND_URL}/api/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = response.data.url.startsWith('http') 
        ? response.data.url 
        : `${BACKEND_URL}${response.data.url}`;
      
      updateField(sectionId, path, imageUrl);
      toast.success('Image uploaded!');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(null);
    }
  };

  // Load all content
  const loadContent = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/content`);
      setSections(response.data.sections || {});
      // Expand first section by default
      const firstSection = Object.keys(response.data.sections || {})[0];
      if (firstSection) {
        setExpandedSections({ [firstSection]: true });
      }
    } catch (error) {
      console.error('Failed to load content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContent();
  }, []);

  // Save a section
  const saveSection = async (sectionId) => {
    setSaving(true);
    try {
      const section = sections[sectionId];
      await axios.put(`${BACKEND_URL}/api/admin/content/${sectionId}`, {
        content: section.content
      });
      toast.success(`${SECTION_NAMES[sectionId] || sectionId} saved!`);
      setHasChanges(prev => ({ ...prev, [sectionId]: false }));
    } catch (error) {
      console.error('Failed to save content:', error);
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  // Reset a section to defaults
  const resetSection = async (sectionId) => {
    if (!window.confirm(`Reset "${SECTION_NAMES[sectionId] || sectionId}" to default content? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/content/${sectionId}`);
      toast.success('Section reset to default');
      loadContent();
    } catch (error) {
      console.error('Failed to reset section:', error);
      toast.error('Failed to reset section');
    }
  };

  // Update a nested field in section content
  const updateField = (sectionId, path, value) => {
    setSections(prev => {
      const updated = { ...prev };
      const section = { ...updated[sectionId] };
      const content = { ...section.content };
      
      // Handle nested paths like "stats.value" or "items[0].title"
      const pathParts = path.split('.');
      let current = content;
      
      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        // Handle array indices like "items[0]"
        const arrayMatch = part.match(/^(.+)\[(\d+)\]$/);
        if (arrayMatch) {
          const [, key, index] = arrayMatch;
          current[key] = [...current[key]];
          current = current[key];
          current[parseInt(index)] = { ...current[parseInt(index)] };
          current = current[parseInt(index)];
        } else {
          current[part] = { ...current[part] };
          current = current[part];
        }
      }
      
      const lastPart = pathParts[pathParts.length - 1];
      const lastArrayMatch = lastPart.match(/^(.+)\[(\d+)\]$/);
      if (lastArrayMatch) {
        const [, key, index] = lastArrayMatch;
        current[key] = [...current[key]];
        current[key][parseInt(index)] = value;
      } else {
        current[lastPart] = value;
      }
      
      section.content = content;
      updated[sectionId] = section;
      return updated;
    });
    setHasChanges(prev => ({ ...prev, [sectionId]: true }));
  };

  // Start inline editing
  const startEditing = (sectionId, path, currentValue) => {
    setEditingField({ sectionId, path });
    setEditValue(currentValue);
  };

  // Save inline edit
  const saveEdit = () => {
    if (editingField) {
      updateField(editingField.sectionId, editingField.path, editValue);
      setEditingField(null);
      setEditValue('');
    }
  };

  // Cancel inline edit
  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  // Render an image upload field
  const renderImageField = (sectionId, path, currentUrl, label) => {
    const isUploading = uploadingImage === `${sectionId}-${path}`;
    const inputId = `image-${sectionId}-${path}`;
    
    return (
      <div className="space-y-2">
        {currentUrl && (
          <div className="relative inline-block">
            <img 
              src={currentUrl.startsWith('http') ? currentUrl : `${BACKEND_URL}${currentUrl}`}
              alt={label}
              className="w-32 h-32 object-cover rounded-xl border border-border"
            />
            <button
              onClick={() => updateField(sectionId, path, '')}
              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
              title="Remove image"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
        <div className="flex items-center gap-2">
          <label
            htmlFor={inputId}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${
              isUploading 
                ? 'bg-secondary text-muted-foreground' 
                : 'bg-primary/10 text-primary hover:bg-primary/20'
            }`}
          >
            {isUploading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <ImagePlus className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">
              {isUploading ? 'Uploading...' : currentUrl ? 'Change Image' : 'Upload Image'}
            </span>
          </label>
          <input
            id={inputId}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) uploadImage(file, sectionId, path);
              e.target.value = '';
            }}
            disabled={isUploading}
          />
          {currentUrl && (
            <input
              type="text"
              value={currentUrl}
              onChange={(e) => updateField(sectionId, path, e.target.value)}
              placeholder="Or paste image URL"
              className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm"
            />
          )}
        </div>
      </div>
    );
  };

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Render an editable field
  const renderEditableField = (sectionId, path, value, label, multiline = false) => {
    const isEditing = editingField?.sectionId === sectionId && editingField?.path === path;
    
    if (isEditing) {
      return (
        <div className="flex items-start gap-2">
          {multiline ? (
            <textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border border-primary bg-background text-sm resize-none min-h-[100px]"
              autoFocus
              data-testid={`edit-field-${path}`}
            />
          ) : (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border border-primary bg-background text-sm"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveEdit();
                if (e.key === 'Escape') cancelEdit();
              }}
              data-testid={`edit-field-${path}`}
            />
          )}
          <button
            onClick={saveEdit}
            className="p-2 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
            data-testid={`save-field-${path}`}
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            onClick={cancelEdit}
            className="p-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      );
    }

    return (
      <div
        onClick={() => startEditing(sectionId, path, value)}
        className="group cursor-pointer px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors border border-transparent hover:border-border"
        data-testid={`field-${path}`}
      >
        <div className="flex items-start justify-between gap-2">
          <span className={`text-sm ${multiline ? 'whitespace-pre-wrap' : 'truncate'}`}>
            {value || <span className="text-muted-foreground italic">Click to add {label}</span>}
          </span>
          <Edit3 className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
        </div>
      </div>
    );
  };

  // Render array items (features, testimonials, etc.)
  const renderArrayItems = (sectionId, items, itemFields) => {
    return (
      <div className="space-y-4">
        {items.map((item, index) => (
          <div key={index} className="p-4 rounded-xl bg-secondary/30 border border-border/40">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-muted-foreground">Item {index + 1}</span>
            </div>
            <div className="space-y-3">
              {itemFields.map(field => (
                <div key={field.key}>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    {field.label}
                  </label>
                  {renderEditableField(
                    sectionId,
                    `items[${index}].${field.key}`,
                    item[field.key],
                    field.label,
                    field.multiline
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render section content based on its type
  const renderSectionContent = (sectionId, section) => {
    const content = section.content || {};
    
    switch (sectionId) {
      case 'hero':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Hero Image</label>
              {renderImageField(sectionId, 'image', content.image, 'Hero Image')}
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Badge Text</label>
              {renderEditableField(sectionId, 'badge', content.badge, 'badge text')}
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Main Title</label>
              {renderEditableField(sectionId, 'title', content.title, 'title')}
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Subtitle</label>
              {renderEditableField(sectionId, 'subtitle', content.subtitle, 'subtitle', true)}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Primary CTA</label>
                {renderEditableField(sectionId, 'cta_primary', content.cta_primary, 'button text')}
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Secondary CTA</label>
                {renderEditableField(sectionId, 'cta_secondary', content.cta_secondary, 'button text')}
              </div>
            </div>
            {content.stats && (
              <div className="p-4 rounded-xl bg-secondary/30 border border-border/40">
                <h4 className="text-sm font-medium mb-3">Stats Badge</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Value</label>
                    {renderEditableField(sectionId, 'stats.value', content.stats.value, 'value')}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Label</label>
                    {renderEditableField(sectionId, 'stats.label', content.stats.label, 'label')}
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'features':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Section Title</label>
              {renderEditableField(sectionId, 'title', content.title, 'title')}
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Section Subtitle</label>
              {renderEditableField(sectionId, 'subtitle', content.subtitle, 'subtitle', true)}
            </div>
            <h4 className="text-sm font-medium mt-6 mb-3">Feature Items</h4>
            {content.items && renderArrayItems(sectionId, content.items, [
              { key: 'title', label: 'Title' },
              { key: 'description', label: 'Description', multiline: true },
              { key: 'icon', label: 'Icon Name (e.g., Zap, Image, Shield)' }
            ])}
          </div>
        );

      case 'pricing':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Section Title</label>
              {renderEditableField(sectionId, 'title', content.title, 'title')}
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Section Subtitle</label>
              {renderEditableField(sectionId, 'subtitle', content.subtitle, 'subtitle')}
            </div>
            <h4 className="text-sm font-medium mt-6 mb-3">Pricing Plans</h4>
            {content.plans && content.plans.map((plan, index) => (
              <div key={index} className="p-4 rounded-xl bg-secondary/30 border border-border/40">
                <h5 className="font-medium mb-3">{plan.name || `Plan ${index + 1}`}</h5>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Name</label>
                      {renderEditableField(sectionId, `plans[${index}].name`, plan.name, 'name')}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Price</label>
                      {renderEditableField(sectionId, `plans[${index}].price`, plan.price, 'price')}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
                    {renderEditableField(sectionId, `plans[${index}].description`, plan.description, 'description')}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">CTA Button Text</label>
                    {renderEditableField(sectionId, `plans[${index}].cta`, plan.cta, 'button text')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'testimonials':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Section Title</label>
              {renderEditableField(sectionId, 'title', content.title, 'title')}
            </div>
            <h4 className="text-sm font-medium mt-6 mb-3">Testimonials</h4>
            {content.items && content.items.map((item, index) => (
              <div key={index} className="p-4 rounded-xl bg-secondary/30 border border-border/40">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-muted-foreground">Testimonial {index + 1}</span>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Profile Photo</label>
                    {renderImageField(sectionId, `items[${index}].image`, item.image, 'Profile')}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Quote</label>
                    {renderEditableField(sectionId, `items[${index}].quote`, item.quote, 'quote', true)}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Author Name</label>
                      {renderEditableField(sectionId, `items[${index}].author`, item.author, 'name')}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Role/Title</label>
                      {renderEditableField(sectionId, `items[${index}].role`, item.role, 'role')}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Company</label>
                      {renderEditableField(sectionId, `items[${index}].company`, item.company, 'company')}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'contact':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Section Title</label>
              {renderEditableField(sectionId, 'title', content.title, 'title')}
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Section Subtitle</label>
              {renderEditableField(sectionId, 'subtitle', content.subtitle, 'subtitle', true)}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Email</label>
                {renderEditableField(sectionId, 'email', content.email, 'email')}
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Phone</label>
                {renderEditableField(sectionId, 'phone', content.phone, 'phone')}
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Website</label>
                {renderEditableField(sectionId, 'website', content.website, 'website')}
              </div>
            </div>
          </div>
        );

      case 'about_hero':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">About Page Image</label>
              {renderImageField(sectionId, 'image', content.image, 'About Page Image')}
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Page Title</label>
              {renderEditableField(sectionId, 'title', content.title, 'title')}
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Subtitle</label>
              {renderEditableField(sectionId, 'subtitle', content.subtitle, 'subtitle', true)}
            </div>
          </div>
        );

      case 'about_story':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Section Title</label>
              {renderEditableField(sectionId, 'title', content.title, 'title')}
            </div>
            <h4 className="text-sm font-medium mt-6 mb-3">Story Paragraphs</h4>
            {content.paragraphs && content.paragraphs.map((paragraph, index) => (
              <div key={index}>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Paragraph {index + 1}</label>
                {renderEditableField(sectionId, `paragraphs[${index}]`, paragraph, 'paragraph', true)}
              </div>
            ))}
          </div>
        );

      default:
        return (
          <div className="text-sm text-muted-foreground">
            <p>Edit the JSON content directly:</p>
            <pre className="mt-2 p-4 bg-secondary/30 rounded-lg overflow-auto text-xs">
              {JSON.stringify(content, null, 2)}
            </pre>
          </div>
        );
    }
  };

  // Get sections for current tab
  const getCurrentSections = () => {
    const sectionIds = PAGE_GROUPS[activeTab] || [];
    return sectionIds.filter(id => sections[id]).map(id => ({ id, ...sections[id] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 rounded-full hover:bg-secondary transition-colors"
              data-testid="back-to-admin"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Content Editor</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Edit your marketing page content
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary hover:bg-secondary/80 text-sm font-medium transition-colors"
              data-testid="preview-site"
            >
              <Eye className="w-4 h-4" />
              Preview
            </a>
            <button
              onClick={loadContent}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary hover:bg-secondary/80 text-sm font-medium transition-colors"
              data-testid="refresh-content"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Page Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {Object.entries(PAGE_GROUPS).map(([page, sectionIds]) => (
            <button
              key={page}
              onClick={() => setActiveTab(page)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-medium transition-all whitespace-nowrap ${
                activeTab === page
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary/50 text-secondary-foreground hover:bg-secondary'
              }`}
              data-testid={`tab-${page}`}
            >
              {page === 'home' ? <Home className="w-4 h-4" /> : <Info className="w-4 h-4" />}
              {page.charAt(0).toUpperCase() + page.slice(1)} Page
              <span className="text-xs opacity-70">({sectionIds.length})</span>
            </button>
          ))}
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {getCurrentSections().map(section => {
            const Icon = SECTION_ICONS[section.id] || FileText;
            const isExpanded = expandedSections[section.id];
            const sectionHasChanges = hasChanges[section.id];

            return (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-2xl border bg-card overflow-hidden ${
                  sectionHasChanges ? 'border-primary/50 ring-2 ring-primary/20' : 'border-border'
                }`}
              >
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full p-4 sm:p-5 flex items-center justify-between hover:bg-secondary/20 transition-colors"
                  data-testid={`section-toggle-${section.id}`}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    )}
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold">{SECTION_NAMES[section.id] || section.id}</h3>
                      <p className="text-xs text-muted-foreground">{section.page} page</p>
                    </div>
                    {sectionHasChanges && (
                      <span className="ml-2 px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                        Unsaved
                      </span>
                    )}
                  </div>
                </button>

                {/* Section Content */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-border/40">
                        <div className="pt-4">
                          {renderSectionContent(section.id, section)}
                        </div>
                        
                        {/* Section Actions */}
                        <div className="flex items-center justify-between mt-6 pt-4 border-t border-border/40">
                          <button
                            onClick={() => resetSection(section.id)}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                            data-testid={`reset-section-${section.id}`}
                          >
                            <RotateCcw className="w-4 h-4" />
                            Reset to Default
                          </button>
                          <button
                            onClick={() => saveSection(section.id)}
                            disabled={saving || !sectionHasChanges}
                            className={`flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                              sectionHasChanges
                                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                                : 'bg-secondary text-muted-foreground cursor-not-allowed'
                            }`}
                            data-testid={`save-section-${section.id}`}
                          >
                            {saving ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Save className="w-4 h-4" />
                            )}
                            Save Changes
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Help Text */}
        <div className="mt-8 p-4 rounded-xl bg-secondary/30 border border-border/40">
          <h4 className="font-medium mb-2 flex items-center gap-2">
            <HelpCircle className="w-4 h-4" />
            How to Use
          </h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Click on any text field to edit it inline</li>
            <li>• Press Enter to save or Escape to cancel</li>
            <li>• Click "Save Changes" to persist your edits to the database</li>
            <li>• Use "Reset to Default" to restore original content</li>
            <li>• Changes are per-section - save each section individually</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ContentEditorPage;
